import axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { Store } from '../../Store';
import { api } from '../../Api';
import { getError } from '../../utils';
import './AdminTreeScreen.css';
import { lockScroll, unlockScroll } from '../../scrollLock';

// Admin genealogy viewer. Opened in its own tab from Admin → Users
// (#/admin/tree/:username) and drawn full screen over the admin chrome.
//
// The placement tree is binary (left/right under `placement`). It is far too
// big to load whole, so the screen shows:
//   * the focus user's upline as a vertical chain ABOVE them (scroll up), the
//     nearest few first, more on demand;
//   * the downline BELOW them (scroll down), a few levels at a time — each
//     leaf that has more people under it gets an expand button;
//   * whole-subtree statistics, computed once per focus user on the server.
// Pan with drag, zoom with the wheel / buttons / pinch, double-click a node to
// make it the new focus ("zoom in to go in").

const CARD_W = 214;
const CARD_H = 124;
const SLOT_W = 120; // empty left/right slot
const SLOT_H = 54;
const H_GAP = 26;
const V_GAP = 72;
const UP_W = 236;
const UP_H = 64;
const UP_GAP = 34;
const LEVELS_PER_LOAD = 3;
const UPLINE_STEP = 10;
const MIN_K = 0.08;
const MAX_K = 2.5;
const SEARCH_EXPAND_LIMIT = 14; // deeper matches re-focus instead of expanding

const nf = (n) => (Number(n) || 0).toLocaleString('en-US');
const fmtDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  return Number.isNaN(x.getTime())
    ? '—'
    : x.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const clamp = (v, a, b) => Math.min(Math.max(v, a), b);

function statusOf(n) {
  if (!n) return { key: 'unknown', label: 'Unknown' };
  if (n.isBlocked) return { key: 'blocked', label: 'Blocked' };
  if (n.status === false) return { key: 'disabled', label: 'Disabled' };
  if (n.isActiveId) return { key: 'active', label: 'Active' };
  return { key: 'inactive', label: 'Inactive' };
}

function initials(n) {
  const s = (n?.name || n?.username || '?').trim();
  const parts = s.split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
}

function treeLink(username) {
  return `${window.location.origin}${window.location.pathname}#/admin/tree/${encodeURIComponent(username)}`;
}

// ---- layout ---------------------------------------------------------------

// Tidy binary layout: a subtree is as wide as its children side by side, a
// parent is centred over them. Both sides are always drawn once a node has any
// child, so an empty left/right leg still shows as an "empty" slot.
function layoutDownline(rootName, childrenOf, isOpen) {
  const kidsMemo = new Map();
  const kids = (u) => {
    if (kidsMemo.has(u)) return kidsMemo.get(u);
    let out = [];
    const list = isOpen(u) ? childrenOf.get(u) || [] : [];
    if (list.length) {
      const left = list.find((n) => n.position === 'left');
      const right = list.find((n) => n.position === 'right');
      const extra = list.filter((n) => n !== left && n !== right);
      out = [
        left ? { node: left } : { empty: 'left' },
        right ? { node: right } : { empty: 'right' },
        ...extra.map((n) => ({ node: n })),
      ];
    }
    kidsMemo.set(u, out);
    return out;
  };

  const widthMemo = new Map();
  const visiting = new Set();
  const width = (u) => {
    if (widthMemo.has(u)) return widthMemo.get(u);
    if (visiting.has(u)) return CARD_W; // cycle guard on corrupt data
    visiting.add(u);
    const ks = kids(u);
    let w = 0;
    ks.forEach((k, i) => {
      w += (k.node ? width(k.node.username) : SLOT_W) + (i ? H_GAP : 0);
    });
    visiting.delete(u);
    w = Math.max(w, CARD_W);
    widthMemo.set(u, w);
    return w;
  };

  const pos = new Map();
  const slots = [];
  const edges = [];
  const placed = new Set();
  const place = (u, cx, y, depth) => {
    if (placed.has(u)) return;
    placed.add(u);
    pos.set(u, { x: cx, y, depth });
    const ks = kids(u);
    if (!ks.length) return;
    const widths = ks.map((k) => (k.node ? width(k.node.username) : SLOT_W));
    const span = widths.reduce((a, b) => a + b, 0) + H_GAP * (ks.length - 1);
    let left = cx - span / 2;
    const cy = y + CARD_H + V_GAP;
    ks.forEach((k, i) => {
      const ccx = left + widths[i] / 2;
      if (k.node) {
        edges.push({ key: `${u}>${k.node.username}`, x1: cx, y1: y + CARD_H, x2: ccx, y2: cy, side: k.node.position });
        place(k.node.username, ccx, cy, depth + 1);
      } else {
        slots.push({ key: `${u}:${k.empty}`, parent: u, side: k.empty, x: ccx, y: cy + (CARD_H - SLOT_H) / 2 });
        edges.push({ key: `${u}>${k.empty}`, x1: cx, y1: y + CARD_H, x2: ccx, y2: cy + (CARD_H - SLOT_H) / 2, side: k.empty, empty: true });
      }
      left += widths[i] + H_GAP;
    });
  };
  place(rootName, 0, 0, 0);
  return { pos, slots, edges };
}

function curve(e) {
  const my = (e.y1 + e.y2) / 2;
  return `M${e.x1},${e.y1} C${e.x1},${my} ${e.x2},${my} ${e.x2},${e.y2}`;
}

// ---- small pieces -----------------------------------------------------------

function Badges({ n, designations, compact }) {
  const desig = n.designation ? designations[n.designation] || `Rank ${n.designation}` : null;
  return (
    <div className="gt-badges">
      {desig && <span className="gt-badge gt-badge-rank" title="Designation">{desig}</span>}
      {n.isAssociation && (
        <span className="gt-badge gt-badge-merchant" title={n.associationName || 'Merchant'}>
          {compact ? 'M' : 'Merchant'}
        </span>
      )}
      {n.isAgent && <span className="gt-badge gt-badge-agent">{compact ? 'A' : 'Agent'}</span>}
    </div>
  );
}

function NodeCard({ n, designations, isRoot, selected, hit, canExpand, isOpenNode, loading, onSelect, onFocus, onToggle }) {
  const st = statusOf(n);
  return (
    <div
      className={`gt-card gt-st-${st.key}${isRoot ? ' gt-root' : ''}${selected ? ' gt-selected' : ''}${hit ? ' gt-hit' : ''}`}
      style={{ left: n._x - CARD_W / 2, top: n._y, width: CARD_W, height: CARD_H }}
      onClick={(e) => { e.stopPropagation(); onSelect(n); }}
      onDoubleClick={(e) => { e.stopPropagation(); if (!isRoot) onFocus(n.username); }}
      title={isRoot ? n.username : 'Click: details · Double-click: focus here'}
    >
      {isRoot && <div className="gt-root-tag">FOCUS</div>}
      {!isRoot && n.position && (
        <div className={`gt-side gt-side-${n.position}`}>{n.position === 'left' ? 'L' : 'R'}</div>
      )}
      <div className="gt-card-head">
        <div className="gt-avatar">{initials(n)}</div>
        <div className="gt-card-names">
          <div className="gt-name">{n.name || n.username}</div>
          <div className="gt-username">@{n.username}</div>
        </div>
      </div>
      <Badges n={n} designations={designations} compact />
      <div className="gt-card-meta">
        <span className={`gt-dot gt-dot-${st.key}`} />
        <span>{st.label}</span>
        <span className="gt-sep">·</span>
        <span>{fmtDate(n.createdAt)}</span>
      </div>
      <div className="gt-lr">
        <span className="gt-lr-l" title="Left accounts (stored counter)">L {nf(n.leftCount)}</span>
        <span className="gt-lr-r" title="Right accounts (stored counter)">R {nf(n.rightCount)}</span>
      </div>
      {n.childCount > 0 && (
        <button
          type="button"
          className={`gt-expand${isOpenNode ? ' open' : ''}`}
          onClick={(e) => { e.stopPropagation(); onToggle(n); }}
          onDoubleClick={(e) => e.stopPropagation()}
          title={isOpenNode ? 'Collapse' : canExpand ? `Load ${LEVELS_PER_LOAD} more levels` : 'Expand'}
        >
          {loading ? <i className="fa fa-spinner fa-spin" /> : isOpenNode ? '−' : `+${n.childCount}`}
        </button>
      )}
    </div>
  );
}

function UplineCard({ n, level, x, y, designations, selected, onSelect, onFocus }) {
  const st = statusOf(n);
  return (
    <div
      className={`gt-upcard gt-st-${st.key}${selected ? ' gt-selected' : ''}`}
      style={{ left: x - UP_W / 2, top: y, width: UP_W, height: UP_H }}
      onClick={(e) => { e.stopPropagation(); onSelect(n); }}
      onDoubleClick={(e) => { e.stopPropagation(); onFocus(n.username); }}
      title="Click: details · Double-click: focus here"
    >
      <div className="gt-uplevel" title={`${level} level(s) above`}>↑{level}</div>
      <div className="gt-avatar sm">{initials(n)}</div>
      <div className="gt-card-names">
        <div className="gt-name">{n.name || n.username}</div>
        <div className="gt-username">
          @{n.username}
          {n.designation ? <span className="gt-up-rank"> · {designations[n.designation] || `Rank ${n.designation}`}</span> : null}
        </div>
      </div>
      <span className={`gt-dot gt-dot-${st.key}`} title={st.label} />
    </div>
  );
}

function Stat({ label, value, tone, sub }) {
  return (
    <div className={`gt-stat${tone ? ` gt-tone-${tone}` : ''}`}>
      <div className="gt-stat-v">{value}</div>
      <div className="gt-stat-l">{label}</div>
      {sub ? <div className="gt-stat-s">{sub}</div> : null}
    </div>
  );
}

// ---- screen ---------------------------------------------------------------

export default function AdminTreeScreen() {
  const { username: paramUser } = useParams();
  const focusName = paramUser || '';
  const navigate = useNavigate();
  const { state } = useContext(Store);
  const { userInfo } = state;
  const headers = useMemo(() => ({ Authorization: `Bearer ${userInfo?.token}` }), [userInfo?.token]);

  // Tree data lives in a ref (merged incrementally); `version` re-renders.
  const data = useRef({ nodes: new Map(), loaded: new Set(), collapsed: new Set() });
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const [root, setRoot] = useState(null);
  const [upline, setUpline] = useState([]);
  const [designations, setDesignations] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyNodes, setBusyNodes] = useState(() => new Set());
  const [uplineShown, setUplineShown] = useState(6);
  const [selected, setSelected] = useState(null);
  const [hit, setHit] = useState('');
  const [stats, setStats] = useState(null);
  const [statsState, setStatsState] = useState('idle'); // idle | loading | error
  const [statsError, setStatsError] = useState('');
  const [panelOpen, setPanelOpen] = useState(() => window.innerWidth > 900);
  const [wheelMode, setWheelMode] = useState('zoom'); // zoom | scroll
  const [fullscreen, setFullscreen] = useState(false);

  // View transform: screen = world * k + (x, y)
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const [animate, setAnimate] = useState(false);
  const animTimer = useRef(null);
  const viewportRef = useRef(null);
  const [pendingCenter, setPendingCenter] = useState(null);

  // Keep the ref in step immediately: wheel/pointer events can arrive several
  // times between renders and must build on the latest transform.
  const applyView = useCallback((next) => {
    viewRef.current = next;
    setView(next);
  }, []);

  const animateTo = useCallback((next) => {
    setAnimate(true);
    applyView(next);
    clearTimeout(animTimer.current);
    animTimer.current = setTimeout(() => setAnimate(false), 380);
  }, [applyView]);

  // Lock the page under the overlay.
  useEffect(() => {
    const key = {};
    lockScroll(key);
    return () => unlockScroll(key);
  }, []);

  // ---- loading ----
  const mergeNodes = useCallback((list, levels, parentName) => {
    const d = data.current;
    for (const n of list) {
      d.nodes.set(n.username, { ...(d.nodes.get(n.username) || {}), ...n });
      if (n.depth < levels) d.loaded.add(n.username);
    }
    if (parentName) d.loaded.add(parentName);
  }, []);

  const loadRoot = useCallback(async (name) => {
    setLoading(true);
    setError('');
    setSelected(null);
    setHit('');
    data.current = { nodes: new Map(), loaded: new Set(), collapsed: new Set() };
    try {
      const { data: res } = await axios.get(
        `${api}/api/admin-tree/node/${encodeURIComponent(name)}?levels=${LEVELS_PER_LOAD}`,
        { headers }
      );
      data.current.nodes.set(res.root.username, res.root);
      mergeNodes(res.nodes, LEVELS_PER_LOAD, res.root.username);
      setRoot(res.root);
      setUpline(res.upline || []);
      setDesignations(res.designations || {});
      setUplineShown(Math.min(6, (res.upline || []).length));
      bump();
      const vp = viewportRef.current;
      const vw = vp ? vp.clientWidth : window.innerWidth;
      const vh = vp ? vp.clientHeight : window.innerHeight;
      const k = vw < 700 ? 0.7 : 1;
      applyView({ x: vw / 2, y: Math.round(vh * 0.4), k });
    } catch (err) {
      setError(getError(err));
      setRoot(null);
    } finally {
      setLoading(false);
    }
  }, [headers, mergeNodes, bump, applyView]);

  const loadStats = useCallback(async (name, refresh) => {
    setStatsState('loading');
    setStatsError('');
    try {
      const { data: res } = await axios.get(
        `${api}/api/admin-tree/stats/${encodeURIComponent(name)}${refresh ? '?refresh=1' : ''}`,
        { headers }
      );
      setStats(res);
      setStatsState('idle');
    } catch (err) {
      setStatsError(getError(err));
      setStatsState('error');
    }
  }, [headers]);

  useEffect(() => {
    if (!focusName) return;
    setStats(null);
    loadRoot(focusName);
    loadStats(focusName);
  }, [focusName, loadRoot, loadStats]);

  const loadSubtree = useCallback(async (name, levels = LEVELS_PER_LOAD) => {
    setBusyNodes((s) => new Set(s).add(name));
    try {
      const { data: res } = await axios.get(
        `${api}/api/admin-tree/subtree/${encodeURIComponent(name)}?levels=${levels}`,
        { headers }
      );
      mergeNodes(res.nodes, levels, name);
      data.current.collapsed.delete(name);
      bump();
      return true;
    } catch (err) {
      setError(getError(err));
      return false;
    } finally {
      setBusyNodes((s) => { const n = new Set(s); n.delete(name); return n; });
    }
  }, [headers, mergeNodes, bump]);

  // ---- derived tree ----
  const isOpen = useCallback(
    (u) => data.current.loaded.has(u) && !data.current.collapsed.has(u),
    []
  );

  const layout = useMemo(() => {
    if (!root) return null;
    const childrenOf = new Map();
    for (const n of data.current.nodes.values()) {
      if (!n.placement || n.username === root.username) continue;
      if (!childrenOf.has(n.placement)) childrenOf.set(n.placement, []);
      childrenOf.get(n.placement).push(n);
    }
    const lay = layoutDownline(root.username, childrenOf, isOpen);
    const cards = [];
    for (const [u, p] of lay.pos) {
      const n = data.current.nodes.get(u);
      if (n) cards.push({ ...n, _x: p.x, _y: p.y, _depth: p.depth });
    }
    // Upline chain straight above the focus card.
    const ups = upline.slice(0, uplineShown).map((n, i) => ({
      n,
      level: i + 1,
      x: 0,
      y: -(i + 1) * (UP_H + UP_GAP),
    }));
    const upEdges = ups.map((u, i) => {
      const childPos = i === 0 ? root.position : upline[i - 1].position;
      const y2 = i === 0 ? 0 : ups[i - 1].y;
      return { key: `up${i}`, x1: 0, y1: u.y + UP_H, x2: 0, y2, side: childPos };
    });
    let minX = -CARD_W / 2, maxX = CARD_W / 2, minY = 0, maxY = CARD_H;
    for (const c of cards) {
      minX = Math.min(minX, c._x - CARD_W / 2);
      maxX = Math.max(maxX, c._x + CARD_W / 2);
      maxY = Math.max(maxY, c._y + CARD_H);
    }
    for (const s of lay.slots) maxY = Math.max(maxY, s.y + SLOT_H);
    if (ups.length) minY = ups[ups.length - 1].y - (uplineShown < upline.length ? 60 : 30);
    const levelsVisible = cards.reduce((m, c) => Math.max(m, c._depth), 0);
    return { ...lay, cards, ups, upEdges, bounds: { minX, maxX, minY, maxY }, levelsVisible };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [root, upline, uplineShown, version, isOpen]);

  // Centre on a node once it has been laid out.
  useEffect(() => {
    if (!pendingCenter || !layout) return;
    const vp = viewportRef.current;
    if (!vp) return;
    let wx;
    let wy;
    const p = layout.pos.get(pendingCenter);
    if (p) {
      wx = p.x;
      wy = p.y + CARD_H / 2;
    } else {
      const up = layout.ups.find((u) => u.n.username === pendingCenter);
      if (!up) return;
      wx = up.x;
      wy = up.y + UP_H / 2;
    }
    const k = Math.max(viewRef.current.k, 0.75);
    animateTo({ k, x: vp.clientWidth / 2 - wx * k, y: vp.clientHeight / 2 - wy * k });
    setPendingCenter(null);
  }, [pendingCenter, layout, animateTo]);

  // ---- view controls ----
  const zoomAt = useCallback((factor, px, py) => {
    const vp = viewportRef.current;
    const v = viewRef.current;
    const cx = px ?? (vp ? vp.clientWidth / 2 : 0);
    const cy = py ?? (vp ? vp.clientHeight / 2 : 0);
    const k = clamp(v.k * factor, MIN_K, MAX_K);
    const r = k / v.k;
    return { k, x: cx - (cx - v.x) * r, y: cy - (cy - v.y) * r };
  }, []);

  const zoomBtn = (f) => animateTo(zoomAt(f));

  const fitToScreen = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp || !layout) return;
    const { minX, maxX, minY, maxY } = layout.bounds;
    const pad = 48;
    const w = maxX - minX;
    const h = maxY - minY;
    const k = clamp(Math.min((vp.clientWidth - pad * 2) / w, (vp.clientHeight - pad * 2) / h), MIN_K, 1.2);
    animateTo({
      k,
      x: (vp.clientWidth - w * k) / 2 - minX * k,
      y: (vp.clientHeight - h * k) / 2 - minY * k,
    });
  }, [layout, animateTo]);

  const resetView = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const k = vp.clientWidth < 700 ? 0.7 : 1;
    animateTo({ x: vp.clientWidth / 2, y: Math.round(vp.clientHeight * 0.4), k });
  }, [animateTo]);

  // Wheel: zoom around the cursor (map style), or scroll the canvas. Ctrl/⌘ +
  // wheel (and trackpad pinch, which arrives as ctrl+wheel) always zooms.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = vp.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const zoom = e.ctrlKey || e.metaKey || wheelMode === 'zoom';
      if (zoom) {
        const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
        const factor = Math.exp(-dy * (e.ctrlKey ? 0.01 : 0.0018));
        applyView(zoomAt(factor, px, py));
      } else {
        const v = viewRef.current;
        const m = e.deltaMode === 1 ? 16 : 1;
        applyView({ ...v, x: v.x - (e.shiftKey ? e.deltaY : e.deltaX) * m, y: v.y - (e.shiftKey ? 0 : e.deltaY) * m });
      }
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, [wheelMode, zoomAt, applyView, root]);

  // Drag to pan; two fingers to pinch-zoom.
  const pointers = useRef(new Map());
  const drag = useRef(null);
  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0 && e.pointerType === 'mouse') return;
    if (e.target.closest('button, a, input')) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const v = viewRef.current;
    if (pointers.current.size === 1) {
      drag.current = { sx: e.clientX, sy: e.clientY, vx: v.x, vy: v.y, moved: false };
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      drag.current = { pinch: true, dist: Math.hypot(a.x - b.x, a.y - b.y), k: v.k, vx: v.x, vy: v.y, mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2, moved: true };
    }
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId) || !drag.current) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const d = drag.current;
    if (d.pinch && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const k = clamp(d.k * (dist / (d.dist || 1)), MIN_K, MAX_K);
      const rect = viewportRef.current.getBoundingClientRect();
      const mx = d.mx - rect.left;
      const my = d.my - rect.top;
      const r = k / d.k;
      applyView({ k, x: mx - (mx - d.vx) * r, y: my - (my - d.vy) * r });
      return;
    }
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < 4) return;
    if (!d.moved) {
      d.moved = true;
      try { viewportRef.current.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
    }
    applyView({ k: viewRef.current.k, x: d.vx + dx, y: d.vy + dy });
  };
  const suppressClick = useRef(false);
  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (drag.current?.moved) {
      suppressClick.current = true;
      setTimeout(() => { suppressClick.current = false; }, 0);
    }
    if (pointers.current.size === 0) drag.current = null;
  };
  const onClickCapture = (e) => {
    if (suppressClick.current) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  // ---- actions ----
  const focusOn = useCallback((u) => {
    if (!u || u === root?.username) return;
    navigate(`/admin/tree/${encodeURIComponent(u)}`);
  }, [navigate, root]);

  const toggleNode = useCallback(async (n) => {
    const d = data.current;
    if (d.loaded.has(n.username)) {
      if (d.collapsed.has(n.username)) d.collapsed.delete(n.username);
      else d.collapsed.add(n.username);
      bump();
    } else {
      await loadSubtree(n.username);
    }
  }, [bump, loadSubtree]);

  const expandAll = async () => {
    // Opens every already-loaded node and loads one more batch under the
    // current leaves — bounded so a click cannot pull thousands of nodes.
    const d = data.current;
    d.collapsed.clear();
    const leaves = [...d.nodes.values()].filter((n) => n.childCount > 0 && !d.loaded.has(n.username));
    bump();
    if (leaves.length > 16) {
      setError(`There are ${leaves.length} unopened branches — open them one at a time, or focus deeper.`);
      return;
    }
    for (const l of leaves) await loadSubtree(l.username, 2);
  };

  const collapseAll = () => {
    const d = data.current;
    for (const n of d.nodes.values()) if (n.username !== root?.username && d.loaded.has(n.username)) d.collapsed.add(n.username);
    bump();
    resetView();
  };

  // ---- search ----
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const runSearch = async (e) => {
    e?.preventDefault();
    const text = q.trim();
    if (!text || !root) return;
    // Already on screen? jump straight there.
    const local = data.current.nodes.get(text) || upline.find((u) => u.username === text);
    setSearching(true);
    try {
      const { data: res } = await axios.get(
        `${api}/api/admin-tree/search?root=${encodeURIComponent(root.username)}&q=${encodeURIComponent(text)}`,
        { headers }
      );
      const ups = upline
        .filter((u) => u.username.toLowerCase().startsWith(text.toLowerCase()) || (u.name || '').toLowerCase().startsWith(text.toLowerCase()))
        .slice(0, 5)
        .map((u) => ({ user: u, upline: true, level: upline.indexOf(u) + 1 }));
      const list = [...ups, ...(res.matches || [])];
      if (list.length === 1 || (local && list.length)) {
        pickResult(list.find((r) => r.user.username === text) || list[0]);
      } else {
        setResults(list);
      }
    } catch (err) {
      setError(getError(err));
    } finally {
      setSearching(false);
    }
  };

  const pickResult = async (r) => {
    setResults(null);
    if (!r) return;
    const name = r.user.username;
    if (r.upline) {
      const idx = upline.findIndex((u) => u.username === name);
      if (idx >= uplineShown) setUplineShown(idx + 1);
      setSelected(r.user);
      setHit(name);
      setPendingCenter(name);
      return;
    }
    if (r.path.length - 1 > SEARCH_EXPAND_LIMIT) {
      // Too deep to unfold level by level — make it the focus; the upline
      // chain above it then shows the whole way back here.
      focusOn(name);
      return;
    }
    const d = data.current;
    for (let i = 0; i < r.path.length - 1; i++) {
      const u = r.path[i];
      d.collapsed.delete(u);
      if (!d.loaded.has(u)) {
        // eslint-disable-next-line no-await-in-loop
        const ok = await loadSubtree(u, 1);
        if (!ok) return;
      }
    }
    bump();
    setSelected(d.nodes.get(name) || r.user);
    setHit(name);
    setPendingCenter(name);
  };

  // ---- keyboard ----
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest && e.target.closest('input, textarea')) return;
      const v = viewRef.current;
      const step = 80;
      if (e.key === '+' || e.key === '=') animateTo(zoomAt(1.25));
      else if (e.key === '-' || e.key === '_') animateTo(zoomAt(0.8));
      else if (e.key === '0') resetView();
      else if (e.key === 'f' || e.key === 'F') fitToScreen();
      else if (e.key === 'ArrowUp') applyView({ ...v, y: v.y + step });
      else if (e.key === 'ArrowDown') applyView({ ...v, y: v.y - step });
      else if (e.key === 'ArrowLeft') applyView({ ...v, x: v.x + step });
      else if (e.key === 'ArrowRight') applyView({ ...v, x: v.x - step });
      else if (e.key === 'Escape') { setResults(null); setSelected(null); }
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [animateTo, zoomAt, resetView, fitToScreen, applyView]);

  useEffect(() => {
    const onFs = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);
  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.().catch(() => {});
  };

  // Background dot grid follows the pan/zoom.
  const gridSize = 26 * view.k;
  const bgStyle = {
    backgroundSize: `${gridSize}px ${gridSize}px`,
    backgroundPosition: `${view.x}px ${view.y}px`,
  };

  // Breadcrumb: tree root … nearest parents → focus.
  const crumbs = useMemo(() => {
    const chain = [...upline].reverse(); // top of tree first
    if (chain.length <= 6) return chain.map((n) => ({ n }));
    return [{ n: chain[0] }, { gap: chain.length - 5 }, ...chain.slice(-4).map((n) => ({ n }))];
  }, [upline]);

  const sel = selected;
  const selSt = statusOf(sel);
  const selIsUp = sel && upline.some((u) => u.username === sel.username);

  const leftTotal = stats?.left?.total ?? root?.leftCount ?? 0;
  const rightTotal = stats?.right?.total ?? root?.rightCount ?? 0;
  const lrSum = leftTotal + rightTotal || 1;
  const maxLevelTotal = stats?.levels?.length ? Math.max(...stats.levels.map((l) => l.total)) : 1;

  return (
    <div className="gt-screen">
      <Helmet>
        <title>{focusName ? `Tree · ${focusName}` : 'Tree view'}</title>
      </Helmet>

      {/* ---------- top bar ---------- */}
      <div className="gt-top">
        <div className="gt-brand">
          <i className="fa fa-sitemap" />
          <span>Tree view</span>
        </div>
        <nav className="gt-crumbs" aria-label="Upline path">
          {crumbs.map((c, i) =>
            c.gap ? (
              <button key="gap" type="button" className="gt-crumb gap" onClick={() => { setUplineShown(upline.length); fitToScreen(); }} title="Show the whole upline">
                … {c.gap} more …
              </button>
            ) : (
              <React.Fragment key={c.n.username}>
                <button type="button" className="gt-crumb" onClick={() => focusOn(c.n.username)} title={c.n.name}>
                  {c.n.username}
                </button>
                <i className="fa fa-angle-right gt-crumb-sep" />
              </React.Fragment>
            )
          )}
          {crumbs.length > 0 && crumbs[crumbs.length - 1].gap && <i className="fa fa-angle-right gt-crumb-sep" />}
          <span className="gt-crumb current">{focusName}</span>
        </nav>
        <form className="gt-search" onSubmit={runSearch}>
          <i className="fa fa-search" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); if (!e.target.value) setResults(null); }}
            placeholder="Find username / name in this tree"
            aria-label="Search in tree"
          />
          {searching && <i className="fa fa-spinner fa-spin" />}
          {results && (
            <div className="gt-results">
              {results.length === 0 && <div className="gt-result empty">No one by that name in this upline or downline.</div>}
              {results.map((r) => (
                <button type="button" key={(r.upline ? 'u' : 'd') + r.user.username} className="gt-result" onClick={() => pickResult(r)}>
                  <span className={`gt-dot gt-dot-${statusOf(r.user).key}`} />
                  <span className="gt-result-main">
                    <b>{r.user.username}</b> <span className="gt-muted">{r.user.name}</span>
                  </span>
                  <span className="gt-result-where">
                    {r.upline ? `upline ↑${r.level}` : r.depth === 0 ? 'focus' : `${r.rootSide === 'right' ? 'Right' : 'Left'} · level ${r.depth}`}
                  </span>
                </button>
              ))}
            </div>
          )}
        </form>
        <div className="gt-actions">
          <button type="button" className="gt-btn" onClick={() => setPanelOpen((o) => !o)} title="Statistics panel">
            <i className="fa fa-chart-bar" /> <span className="gt-hide-sm">Stats</span>
          </button>
          <button type="button" className="gt-btn" onClick={toggleFullscreen} title="Browser full screen">
            <i className={`fa ${fullscreen ? 'fa-compress' : 'fa-expand'}`} />
          </button>
          <button type="button" className="gt-btn" onClick={() => window.close()} title="Close this tab">
            <i className="fa fa-times" />
          </button>
        </div>
      </div>

      <div className="gt-body">
        {/* ---------- canvas ---------- */}
        <div
          ref={viewportRef}
          className="gt-viewport"
          style={bgStyle}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onClickCapture={onClickCapture}
          onClick={() => setResults(null)}
        >
          {loading && (
            <div className="gt-center-msg">
              <i className="fa fa-spinner fa-spin" /> Loading tree for <b>{focusName}</b>…
            </div>
          )}
          {!loading && !root && (
            <div className="gt-center-msg error">
              <i className="fa fa-exclamation-triangle" /> {error || 'User not found.'}
            </div>
          )}

          {root && layout && (
            <div
              className={`gt-layer${animate ? ' gt-anim' : ''}`}
              style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})` }}
            >
              <svg className="gt-edges" width="1" height="1">
                {layout.upEdges.map((e) => (
                  <g key={e.key}>
                    <line x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2} className="gt-edge-up" />
                    {e.side && (
                      <text x={e.x1 + 8} y={(e.y1 + e.y2) / 2 + 4} className={`gt-edge-label gt-txt-${e.side}`}>
                        {e.side === 'left' ? 'L' : 'R'}
                      </text>
                    )}
                  </g>
                ))}
                {layout.edges.map((e) => (
                  <path key={e.key} d={curve(e)} className={`gt-edge gt-edge-${e.side}${e.empty ? ' empty' : ''}`} />
                ))}
              </svg>

              {uplineShown < upline.length && (
                <button
                  type="button"
                  className="gt-more-up"
                  style={{
                    left: -UP_W / 2,
                    top: (layout.ups.length ? layout.ups[layout.ups.length - 1].y : 0) - 52,
                    width: UP_W,
                  }}
                  onClick={(e) => { e.stopPropagation(); setUplineShown((s) => Math.min(upline.length, s + UPLINE_STEP)); }}
                >
                  <i className="fa fa-chevron-up" /> Show {Math.min(UPLINE_STEP, upline.length - uplineShown)} more upline
                  <span className="gt-muted"> ({upline.length - uplineShown} hidden)</span>
                </button>
              )}
              {uplineShown >= upline.length && upline.length > 0 && (
                <div
                  className="gt-top-of-tree"
                  style={{ left: -UP_W / 2, top: layout.ups[layout.ups.length - 1].y - 34, width: UP_W }}
                >
                  Top of the tree
                </div>
              )}
              {uplineShown > 6 && (
                <button
                  type="button"
                  className="gt-less-up"
                  style={{ left: UP_W / 2 + 14, top: layout.ups[layout.ups.length - 1].y + 14 }}
                  onClick={(e) => { e.stopPropagation(); setUplineShown(6); }}
                  title="Collapse the upline chain"
                >
                  <i className="fa fa-compress" /> collapse
                </button>
              )}

              {layout.ups.map((u) => (
                <UplineCard
                  key={u.n.username}
                  {...u}
                  designations={designations}
                  selected={sel?.username === u.n.username}
                  onSelect={setSelected}
                  onFocus={focusOn}
                />
              ))}

              {layout.cards.map((n) => (
                <NodeCard
                  key={n.username}
                  n={n}
                  designations={designations}
                  isRoot={n.username === root.username}
                  selected={sel?.username === n.username}
                  hit={hit === n.username}
                  canExpand={!data.current.loaded.has(n.username)}
                  isOpenNode={isOpen(n.username) && n.childCount > 0}
                  loading={busyNodes.has(n.username)}
                  onSelect={setSelected}
                  onFocus={focusOn}
                  onToggle={toggleNode}
                />
              ))}

              {layout.slots.map((s) => (
                <div
                  key={s.key}
                  className={`gt-slot gt-slot-${s.side}`}
                  style={{ left: s.x - SLOT_W / 2, top: s.y, width: SLOT_W, height: SLOT_H }}
                >
                  Empty {s.side}
                </div>
              ))}
            </div>
          )}

          {/* zoom controls */}
          <div className="gt-zoom" onPointerDown={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => zoomBtn(1.25)} title="Zoom in (+)"><i className="fa fa-plus" /></button>
            <div className="gt-zoom-pct">{Math.round(view.k * 100)}%</div>
            <button type="button" onClick={() => zoomBtn(0.8)} title="Zoom out (−)"><i className="fa fa-minus" /></button>
            <hr />
            <button type="button" onClick={fitToScreen} title="Fit to screen (F)"><i className="fa fa-expand-arrows-alt" /></button>
            <button type="button" onClick={resetView} title="Reset view (0)"><i className="fa fa-crosshairs" /></button>
            <hr />
            <button type="button" onClick={() => animateTo({ ...viewRef.current, y: viewRef.current.y + 300 })} title="Scroll to upline (↑)"><i className="fa fa-arrow-up" /></button>
            <button type="button" onClick={() => animateTo({ ...viewRef.current, y: viewRef.current.y - 300 })} title="Scroll to downline (↓)"><i className="fa fa-arrow-down" /></button>
          </div>

          <div className="gt-hud" onPointerDown={(e) => e.stopPropagation()}>
            <div className="gt-seg" role="group" aria-label="Mouse wheel">
              <span>Wheel:</span>
              <button type="button" className={wheelMode === 'zoom' ? 'on' : ''} onClick={() => setWheelMode('zoom')}>Zoom</button>
              <button type="button" className={wheelMode === 'scroll' ? 'on' : ''} onClick={() => setWheelMode('scroll')}>Scroll</button>
            </div>
            <button type="button" className="gt-btn sm" onClick={expandAll} title="Open everything loaded + one more batch under the leaves">
              <i className="fa fa-angle-double-down" /> Expand
            </button>
            <button type="button" className="gt-btn sm" onClick={collapseAll} title="Collapse to the focus user">
              <i className="fa fa-angle-double-up" /> Collapse
            </button>
            {layout && (
              <span className="gt-muted gt-hide-sm">
                {nf(layout.cards.length)} shown · {layout.levelsVisible} level{layout.levelsVisible === 1 ? '' : 's'} open
              </span>
            )}
          </div>

          <div className="gt-legend" onPointerDown={(e) => e.stopPropagation()}>
            <span><i className="gt-dot gt-dot-active" /> Active</span>
            <span><i className="gt-dot gt-dot-inactive" /> Inactive</span>
            <span><i className="gt-dot gt-dot-blocked" /> Blocked</span>
            <span><i className="gt-dot gt-dot-disabled" /> Disabled</span>
            <span className="gt-hide-sm"><i className="gt-line gt-line-left" /> Left</span>
            <span className="gt-hide-sm"><i className="gt-line gt-line-right" /> Right</span>
            <span className="gt-muted gt-hide-sm">Drag to pan · Double-click a card to focus</span>
          </div>

          {error && root && (
            <div className="gt-toast" onClick={() => setError('')}>
              <i className="fa fa-exclamation-circle" /> {error} <span className="gt-muted">(click to dismiss)</span>
            </div>
          )}
        </div>

        {/* ---------- side panel ---------- */}
        {panelOpen && (
          <aside className="gt-panel">
            <button type="button" className="gt-panel-x" onClick={() => setPanelOpen(false)} aria-label="Close panel">
              <i className="fa fa-times" />
            </button>

            {sel && (
              <section className="gt-sec gt-selcard">
                <div className="gt-sec-title">Selected</div>
                <div className="gt-sel-head">
                  <div className={`gt-avatar lg gt-st-${selSt.key}`}>{initials(sel)}</div>
                  <div>
                    <div className="gt-sel-name">{sel.name || sel.username}</div>
                    <div className="gt-muted">@{sel.username}</div>
                  </div>
                </div>
                <Badges n={sel} designations={designations} />
                <dl className="gt-dl">
                  <dt>Status</dt><dd><span className={`gt-dot gt-dot-${selSt.key}`} /> {selSt.label}</dd>
                  <dt>Joined</dt><dd>{fmtDate(sel.createdAt)}</dd>
                  <dt>Placement</dt><dd>{sel.placement || '—'}{sel.position ? ` (${sel.position})` : ''}</dd>
                  <dt>Sponsor</dt><dd>{sel.reference || '—'}</dd>
                  <dt>Left / Right</dt><dd>{nf(sel.leftCount)} / {nf(sel.rightCount)}</dd>
                  {sel.childCount != null && !selIsUp && (<><dt>Direct legs</dt><dd>{sel.childCount}</dd></>)}
                  {sel.isAssociation && sel.associationName && (<><dt>Merchant</dt><dd>{sel.associationName}</dd></>)}
                </dl>
                <div className="gt-sel-actions">
                  {sel.username !== root?.username && (
                    <button type="button" className="gt-btn primary" onClick={() => focusOn(sel.username)}>
                      <i className="fa fa-bullseye" /> Focus here
                    </button>
                  )}
                  <button type="button" className="gt-btn" onClick={() => setPendingCenter(sel.username)}>
                    <i className="fa fa-crosshairs" /> Center
                  </button>
                  {sel._id && (
                    <a className="gt-btn" href={`${window.location.origin}${window.location.pathname}#/admin/userreport/${sel._id}`} target="_blank" rel="noopener noreferrer">
                      <i className="fa fa-chart-line" /> Report
                    </a>
                  )}
                  <a className="gt-btn" href={treeLink(sel.username)} target="_blank" rel="noopener noreferrer" title="Open this user's tree in another tab">
                    <i className="fa fa-external-link-alt" />
                  </a>
                </div>
              </section>
            )}

            <section className="gt-sec">
              <div className="gt-sec-title">
                Downline of <b>{focusName}</b>
                <button
                  type="button"
                  className="gt-icon-btn"
                  onClick={() => loadStats(focusName, true)}
                  disabled={statsState === 'loading'}
                  title="Recalculate"
                >
                  <i className={`fa fa-sync-alt${statsState === 'loading' ? ' fa-spin' : ''}`} />
                </button>
              </div>

              {statsState === 'loading' && !stats && (
                <div className="gt-muted gt-pad">
                  <i className="fa fa-spinner fa-spin" /> Counting the whole downline… (large trees take a few seconds)
                </div>
              )}
              {statsState === 'error' && (
                <div className="gt-warn">
                  {statsError}{' '}
                  <button type="button" className="gt-link" onClick={() => loadStats(focusName)}>Retry</button>
                </div>
              )}
              {stats?.partial && <div className="gt-warn">{stats.message}</div>}

              <div className="gt-big">
                <div className="gt-big-v">{nf(stats ? stats.total : leftTotal + rightTotal)}</div>
                <div className="gt-big-l">people in downline{!stats ? ' (stored counters)' : ''}</div>
              </div>

              <div className="gt-lrbar" title={`Left ${nf(leftTotal)} · Right ${nf(rightTotal)}`}>
                <div className="gt-lrbar-l" style={{ width: `${(leftTotal / lrSum) * 100}%` }} />
                <div className="gt-lrbar-r" style={{ width: `${(rightTotal / lrSum) * 100}%` }} />
              </div>
              <div className="gt-lrnums">
                <span className="gt-txt-left"><b>{nf(leftTotal)}</b> left</span>
                <span className="gt-txt-right"><b>{nf(rightTotal)}</b> right</span>
              </div>
              {stats && !stats.partial && root && (stats.left.total !== root.leftCount || stats.right.total !== root.rightCount) && (
                <div className="gt-muted gt-small">
                  Stored counters: L {nf(root.leftCount)} / R {nf(root.rightCount)}
                </div>
              )}

              {stats && !stats.partial && (
                <div className="gt-stats">
                  <Stat label="Active" value={nf(stats.active)} tone="green" sub={`L ${nf(stats.left.active)} · R ${nf(stats.right.active)}`} />
                  <Stat label="Inactive" value={nf(stats.total - stats.active)} tone="grey" />
                  <Stat label="Blocked" value={nf(stats.blocked)} tone="red" sub={`L ${nf(stats.left.blocked)} · R ${nf(stats.right.blocked)}`} />
                  <Stat label="Levels deep" value={nf(stats.depth)} tone="blue" />
                  <Stat label="Merchants" value={nf(stats.merchants)} tone="purple" sub={`L ${nf(stats.left.merchants)} · R ${nf(stats.right.merchants)}`} />
                  <Stat label="Agents" value={nf(stats.agents)} tone="teal" />
                  <Stat label="Joined 30 days" value={nf(stats.new30d)} tone="amber" sub={`L ${nf(stats.left.new30d)} · R ${nf(stats.right.new30d)}`} />
                  <Stat label="Upline levels" value={nf(upline.length)} />
                </div>
              )}
              {stats && !stats.partial && stats.computedAt && (
                <div className="gt-muted gt-small">
                  Calculated {new Date(stats.computedAt).toLocaleTimeString()}{stats.cached ? ' (cached)' : ''}
                </div>
              )}
            </section>

            {stats?.levels?.length > 0 && (
              <section className="gt-sec">
                <div className="gt-sec-title">Per level</div>
                <div className="gt-levels">
                  <div className="gt-lvl head">
                    <span>Lvl</span><span>Total</span><span className="gt-txt-left">L</span><span className="gt-txt-right">R</span><span>Fill</span>
                  </div>
                  {stats.levels.map((l) => {
                    const cap = l.level <= 40 ? 2 ** l.level : Infinity;
                    const fill = cap === Infinity ? null : (l.total / cap) * 100;
                    return (
                      <div className="gt-lvl" key={l.level} title={`Level ${l.level}: ${nf(l.total)} people, ${nf(l.active)} active, ${nf(l.blocked)} blocked`}>
                        <span>{l.level}</span>
                        <span className="gt-lvl-total">
                          <i style={{ width: `${(l.total / maxLevelTotal) * 100}%` }} />
                          <b>{nf(l.total)}</b>
                        </span>
                        <span className="gt-txt-left">{nf(l.left)}</span>
                        <span className="gt-txt-right">{nf(l.right)}</span>
                        <span className="gt-muted">{fill == null ? '—' : fill >= 10 ? `${Math.round(fill)}%` : `${fill.toFixed(fill < 0.1 ? 3 : 1)}%`}</span>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {root && (
              <section className="gt-sec">
                <div className="gt-sec-title">Focus user</div>
                <dl className="gt-dl">
                  <dt>Name</dt><dd>{root.name}</dd>
                  <dt>Placement</dt><dd>{root.placement || 'Top of tree'}{root.position ? ` (${root.position})` : ''}</dd>
                  <dt>Sponsor</dt><dd>{root.reference || '—'}</dd>
                  <dt>Joined</dt><dd>{fmtDate(root.createdAt)}</dd>
                </dl>
              </section>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
